'use client'

import clsx from 'clsx'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'

import { useCheckAuth } from '@/features/auth/check'
import { HeaderSearchButton } from '@/features/search/ui/header-search-button/header-search-button'

import { useCart } from '@/entities/cart'

import { navItems } from '@/shared/data/personal/personal'
import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { useIsClient } from '@/shared/lib/hooks/useIsClient'
import { Modal } from '@/shared/ui'
import { DynamicCounter } from '@/shared/ui/Counter/Counter.client'

import { selectors } from '../../../../../store/selectors'

import { CartIcon } from './assets/CartIcon'
import { CompareIcon } from './assets/CompareIcon'
import { FavouritesIcon } from './assets/FavouritesIcon'
import { UserIcon } from './assets/UserIcon'

import cl from './style.module.scss'

// Auth flow (useAuthByPhone, captcha, code/registration steps) is heavy and only
// needed once the user opens the login modal — keep it out of the header's initial bundle.
const Authorization = dynamic(() => import('@/widgets/Authorization').then(m => m.Authorization), {
	ssr: false
})

export const HeaderPersonalLinks = (): JSX.Element => {
	const pathname = usePathname()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const isClient = useIsClient()

	const { isAuthorized } = useCheckAuth()

	const { cartCount } = useCart()

	const itemsCodes = useSelector(selectors.compareItemsCodes)
	const favorites = useSelector(selectors.favorites)
	const links = navItems
		.map(({ name, link }) => ({
			title: name,
			link: link
		}))
		.slice(0, -1)
	const isPersonalPage = pathname.includes('personal')

	const onOpen = () => setIsModalOpen(true)
	const onClose = () => setIsModalOpen(false)

	return (
		<nav className={cl.nav}>
			{/*Поиск*/}
			<HeaderSearchButton exCl={cl.search} />
			{/*Личный кабинет*/}
			<>
				{isAuthorized && (
					<Link
						prefetch={false}
						href={'/personal'}
						className={clsx('gtm-login-icon', cl.item, cl.profile)}
						id={'profileBtn'}
						aria-label={'Личный кабинет'}
						data-tooltip-id={'personal-tooltip'}>
						<UserIcon className="gtm-login-icon" width={24} height={24} />
					</Link>
				)}
				{!isAuthorized && (
					<div
						className={clsx(cl.item, cl.profile)}
						onClick={onOpen}
						id={'loginBtn'}
						data-tooltip-id={'personal-tooltip'}>
						<UserIcon className="gtm-login-icon" width={24} height={24} />
					</div>
				)}
			</>
			{/*Сравнение*/}
			<Link prefetch={false} href={'/compare'} className={cl.item} aria-label={'Сравнение'}>
				{isClient && itemsCodes?.length > 0 && <DynamicCounter count={itemsCodes.length} />}
				<CompareIcon width={24} height={24} />
			</Link>
			{/*Избранное*/}
			<Link prefetch={false} href={'/favorites'} className={cl.item} aria-label={'Избранное'}>
				{isClient && favorites?.length > 0 && <DynamicCounter count={favorites.length} />}
				<FavouritesIcon width={24} height={24} />
			</Link>
			{/*Корзина*/}
			<Link
				prefetch={false}
				href={'/cart'}
				className={cl.item}
				aria-label={'Корзина'}
				onClick={() => sendYandexMetricaEvent(`view_cart`)}>
				{isClient && cartCount > 0 && <DynamicCounter id="cart-counter" count={cartCount} />}
				<CartIcon width={24} height={24} />
			</Link>
			{!isPersonalPage && isAuthorized && (
				<Tooltip
					noArrow={false}
					clickable={true}
					id={'personal-tooltip'}
					opacity={1}
					variant={'light'}
					style={{
						zIndex: 99,
						boxShadow: '0 8px 18px 0 rgba(0, 0, 0, 0.25)',
						padding: '14px 18px',
						borderRadius: '12px'
					}}>
					<ul className={cl.subMenu}>
						{links.map(item => (
							<li key={item.title} className={cl.item}>
								<Link prefetch={false} href={item.link} title={item.title}>
									{/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */}
									{item.title}
								</Link>
							</li>
						))}
					</ul>
				</Tooltip>
			)}
			<Modal fullScreenOnMobile isOpen={isModalOpen} onClose={onClose}>
				{isModalOpen && <Authorization onSuccess={() => onClose()} />}
			</Modal>
		</nav>
	)
}
